import { type ReactNode, useMemo, useState } from 'react'
import { Box, Stack, Tooltip, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { ArrowUpRightIcon, BadgeCheckIcon, EyeOffIcon, FileTextIcon, GlobeIcon, ImageIcon, PlusIcon, RefreshCwIcon, Undo2Icon } from 'lucide-react'

import type { ContentFeedRun } from '@repo/types'
import { Button, EMPTY_VALUE, Skeleton } from '@repo/ui'
import {
  ActionDropdown,
  ConfirmSettingModal,
  EmptyState,
  Header,
  headerWithInfo,
  INFO_HEADER_ALLOWANCE,
  StatusChip,
  Table,
} from 'components'
import { InfoTooltip } from 'components/feedback/InfoTooltip'
import { statusFilterOptions } from 'components/input/filters/StatusChipFilter'
import { ARTICLE_STATES } from 'constants/statuses'
import { useSnackbar } from 'contexts'
import { useCanAccess } from 'hooks'
import {
  useApproveArticle,
  useArticleApprovals,
  useArticlePublications,
  usePublishArticle,
  useRefreshArticleOnWebsite,
  useFeedContentIdeas,
  useRevokeArticleApproval,
  useSeoPages,
  useUnpublishArticle,
} from 'hooks/api'
import type { ArticleApproval, ArticlePublication } from 'services'
import { ROUTES } from 'routes/routes'
import { useCoverApprovals, useCoverImageUrl, useWebsiteManifest } from 'hooks/api/useArticleCovers'
import { articleContentApi } from 'services/coversApi'

import { rangeForPreset } from '../InternalAnalyticsPage/websiteAnalyticsModel'
import { formatIsoDay } from '../GtmTradeShowsPage/tradeShowModel'
import { ARTICLES_MANIFEST } from './articlesManifest.generated'
import {
  approvalKey,
  articleLink,
  articleState,
  articleHasCoverTool,
  coverLabel,
  isWebsiteRefreshPending,
  nextReviewStep,
  performanceByPath,
  PUBLISH_PERMISSION_TOOLTIP,
  publishConsequence,
  publishTitle,
  UNPUBLISH_CONSEQUENCE,
  unpublishTitle,
  type ArticleState,
  type PagePerformance,
} from './articleModel'
import type { ArticleSection, ManifestArticle } from './articleTypes'
import { ContentIdeasPanel } from './ContentIdeasPanel'
import { MANAGE_IDEAS_PERMISSION, MANAGE_IDEAS_TIP } from './contentIdeaModel'
import { EditorialRulesPanel } from './EditorialRulesPanel'

/**
 * `/internal/gtm/articles` — VRG-674: every /insights and /learn piece, and where each stands.
 *
 * THREE SOURCES, AND THE PAGE NEVER CONFUSES THEM.
 *   - The ARTICLES (title, words, cover, content hash) come from vera-website's article
 *     manifest (`content/generated/articles-manifest.json`). VRG-733: read FRESH from the
 *     website's base branch through `/internal/gtm/article-content/manifest` (insights articles,
 *     explainers, and interviews once the website has any), so new covers and explainers show
 *     without a vera-2 change; the committed snapshot (`articlesManifest.generated.ts`) is only
 *     the fallback when that read fails, and the header says which one the rows came from.
 *     A row's title or cover opens the article page (Overview, Cover); the ⋮ menu stays.
 *   - PUBLICATION is vera-2's own (VRG-685, decisions/LOG.md 2026-10-08): Publish / Unpublish
 *     here goes live on vera.inc within about a minute, because the website reads the
 *     published set from `GET /api/public/website/published-articles` and is nudged by a
 *     signed webhook. Everything stays staged until Spencer publishes it. If the webhook did
 *     not land, the row says "Website refresh pending" (the website's own refresh catches up).
 *   - The APPROVAL is vera-2's record (`gtm_article_approvals`): "Spencer read this version
 *     and it may go out". It carries the manifest's content hash, so an edit after approval
 *     reads "Changed since approval" instead of inheriting the approval. Publishing does not
 *     require one; the Publish confirm says when the words changed since one.
 * The page says so only when the fresh read fails, because the published/staged column is then
 * only as fresh as the last regeneration (the source line came off on 2026-11-04, Spencer).
 *
 * PERFORMANCE is read from data vera-2 already has: the SEO "pages to leads" rows (Search
 * Console clicks and impressions joined to website visitors and signups by path), last 90
 * days. A staged article has no traffic by construction; its cells read "—", not 0.
 *
 * Approving is platform_admin only (server-enforced). Publish / Unpublish need the
 * `internal_articles_publish` permission ("Publish articles", seeded to platform_admin); without
 * it they show disabled with the permission named (§27). Both are behind `ConfirmSettingModal`.
 */

/** The website's editorial type as a label; the section mapping is the fallback for a manifest without `type`. */
const articleTypeLabel = (a: Pick<ManifestArticle, 'type' | 'section'>): string =>
  a.type ? a.type.charAt(0).toUpperCase() + a.type.slice(1) : ARTICLE_TYPE_LABELS[a.section]

type Row = ManifestArticle & {
  id: string
  state: ArticleState
  approval: ArticleApproval | undefined
  publication: ArticlePublication | undefined
  refreshPending: boolean
  /** The one thing that moves it forward (`nextReviewStep`); null once live. */
  next: string | null
  perf: PagePerformance | undefined
  updated: string
}

/**
 * VRG-751 — the editorial type each website section holds. Labels match VRG-740's content-idea
 * types (`CONTENT_IDEA_TYPES`), so an idea typed "explainer" and the article it becomes read the
 * same word on the same page.
 */
const ARTICLE_TYPE_LABELS: Record<ArticleSection, string> = { insights: 'Perspective', learn: 'Explainer' }

const RANGE = rangeForPreset('90d')

const day = (iso: string | null | undefined) => (iso ? formatIsoDay(iso.slice(0, 10)) : '')

/** The one line under the status chip: the most relevant fact, then a pending refresh. */
const statusLine = (row: Row): string => {
  const p = row.publication
  if (row.state === 'published') {
    return p?.isPublished ? `Published ${day(p.publishedAt)}` : 'Live via the website (PUBLIC_ROUTES)'
  }
  if (row.approval) {
    return `Approved ${day(row.approval.approvedAt)}${row.approval.approvedBy ? ` by ${row.approval.approvedBy}` : ''}`
  }
  if (p && !p.isPublished) return `Unpublished ${day(p.unpublishedAt)}`
  return ''
}

const count = (v: number | null | undefined) => (v == null ? EMPTY_VALUE : v.toLocaleString('en-US'))

// The SEO pages query loads separately from the snapshot rows; its cells skeleton meanwhile (§19).
const PerfSkeleton = () => (
  <Stack justifyContent="center" height="100%" sx={{ width: '100%' }}>
    <Skeleton height={14} width="60%" />
  </Stack>
)

/** The Articles table: the website snapshot's articles, approval and publication (VRG-674 / VRG-685). */
const ArticlesTable = () => {
  const { open: notify } = useSnackbar()
  const navigate = useNavigate()
  const { isHasAccess: isPlatformAdmin } = useCanAccess({ role: 'platform_admin' })
  const { isHasAccess: canPublish } = useCanAccess({ action: 'internal_articles_publish' })
  const { data: approvals, isLoading: approvalsLoading, error: approvalsError } = useArticleApprovals()
  const { data: publications, isLoading: publicationsLoading, error: publicationsError } = useArticlePublications()
  const { data: pages, isLoading: pagesLoading } = useSeoPages(RANGE)
  const { data: website, isLoading: websiteLoading } = useWebsiteManifest()
  const fresh = website?.available ? website : null
  const openArticle = (row: Pick<Row, 'section' | 'slug'>) => navigate(ROUTES.INTERNAL_GTM_ARTICLE(row.section, row.slug))
  const approve = useApproveArticle()
  const revoke = useRevokeArticleApproval()
  const publish = usePublishArticle()
  const unpublish = useUnpublishArticle()
  const refresh = useRefreshArticleOnWebsite()
  const [confirming, setConfirming] = useState<Row | null>(null)
  const [publishing, setPublishing] = useState<Row | null>(null)
  const [unpublishing, setUnpublishing] = useState<Row | null>(null)
  const publishBusy = publish.isPending || unpublish.isPending || refresh.isPending
  // VRG-733 — locked covers, for Next step. A failed read leaves covers looking unlocked, which
  // errs toward "Lock a cover", never toward a step being skipped.
  const { data: coverLocks } = useCoverApprovals()

  const rows: Row[] = useMemo(() => {
    const byKey = new Map((approvals ?? []).map((a) => [approvalKey(a), a]))
    const pubByKey = new Map((publications ?? []).map((p) => [approvalKey(p), p]))
    const locked = new Set((coverLocks?.approvals ?? []).map((c) => approvalKey(c)))
    const perf = performanceByPath(pages?.rows)
    if (websiteLoading) return []
    const articles = (fresh?.articles ?? ARTICLES_MANIFEST.articles) as ManifestArticle[]
    return articles.map((a) => {
      const approval = byKey.get(approvalKey(a))
      const publication = pubByKey.get(approvalKey(a))
      const state = articleState(a, approval, publication)
      return {
        ...a,
        id: approvalKey(a),
        approval,
        publication,
        refreshPending: isWebsiteRefreshPending(publication),
        state,
        next: nextReviewStep({ section: a.section, state, coverLocked: locked.has(approvalKey(a)), websiteHasImage: a.cover.type === 'image' }),
        perf: perf.get(a.path),
        updated: a.updatedAt ?? a.publishedAt,
      }
    })
  }, [approvals, publications, pages, fresh, websiteLoading, coverLocks])

  const columns = useMemo(
    () => [
      {
        field: 'title',
        headerName: 'Article',
        type: 'string',
        width: 340,
        pinned: 'left',
        disableReorder: true,
        // The shared Table has no onRowClick pass-through, so the title cell (and the cover
        // thumbnail) is the click target: the convention the Market review and Library use.
        renderCell: ({ row }: { row: Row }) => (
          <Stack
            justifyContent="center"
            height="100%"
            role="link"
            tabIndex={0}
            onClick={() => openArticle(row)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') openArticle(row)
            }}
            sx={{ minWidth: 0, cursor: 'pointer', '&:hover .art-title': { textDecoration: 'underline' } }}
          >
            <Typography className="art-title" variant="body3" sx={{ fontWeight: 500 }} noWrap title={row.title}>
              {row.title}
            </Typography>
            <Typography variant="body4" color="text.secondary" noWrap title={row.description}>
              {row.href ?? row.path}
            </Typography>
          </Stack>
        ),
      },
      {
        field: 'state',
        headerName: 'Status',
        type: 'statusChip',
        options: statusFilterOptions(ARTICLE_STATES),
        // 320: "Published Oct 28, 2026 · Website refresh pending" fits on one line (measured).
        width: 320,
        renderCell: ({ row }: { row: Row }) => {
          const line = statusLine(row)
          return (
            <Stack justifyContent="center" height="100%" spacing={1} sx={{ minWidth: 0 }}>
              <StatusChip statuses={ARTICLE_STATES} status={row.state} size="small" />
              {line || row.refreshPending ? (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                  {line ? (
                    <Typography variant="body4" color="text.secondary" noWrap title={line}>
                      {line}
                      {row.refreshPending ? ' ·' : ''}
                    </Typography>
                  ) : null}
                  {row.refreshPending ? (
                    <InfoTooltip
                      term="article-website-refresh-pending"
                      caveat={row.publication?.revalidate.error ?? undefined}
                    >
                      <Typography
                        variant="body4"
                        noWrap
                        sx={{ color: 'warning.main', flexShrink: 0, cursor: 'help' }}
                      >
                        Website refresh pending
                      </Typography>
                    </InfoTooltip>
                  ) : null}
                </Stack>
              ) : null}
            </Stack>
          )
        },
      },
      {
        // VRG-733 (Spencer, 2026-10-29) — the review steps' next move, so the list reads as a queue.
        // The same `nextReviewStep` the article page's strip outlines; "—" once live.
        field: 'next',
        headerName: 'Next step',
        type: 'string',
        width: 180,
        valueGetter: (_: unknown, row: Row) => row.next ?? EMPTY_VALUE,
      },
      {
        // VRG-751 — the editorial TYPE, which is what the two website sections MEAN: /insights is
        // a perspective (analysis, opinion), /learn an explainer (how something works). Spencer
        // chose a Type column + filter over Perspectives / Explainers tabs: one object, one
        // table, one machine (idea → draft → approved → published), split by a filter.
        //
        // VRG-733 merge (Spencer, 2026-10-29: "Type -> explainer, perspective, interview"): the
        // website's manifest now RECORDS the editorial type per piece (`type`: perspective |
        // explainer | interview), so it is read, as VRG-751 asked once it existed (§76). The
        // section mapping is only the fallback for a manifest older than that field. The
        // manifest's `kind` (article | video | explainer) is still a MEDIA type and is not used.
        field: 'type',
        headerName: 'Type',
        type: 'multiSelect',
        width: 130 + INFO_HEADER_ALLOWANCE,
        renderHeader: headerWithInfo(
          'Type',
          'What the piece is: a perspective (analysis and opinion), an explainer (how something works) or an interview. Read from the website’s own type for the piece.',
        ),
        valueGetter: (_: unknown, row: Row) => articleTypeLabel(row),
      },
      {
        field: 'coverType',
        headerName: 'Cover',
        type: 'multiSelect',
        width: 230,
        valueGetter: (_: unknown, row: Row) => coverLabel(row.cover, row.section),
        renderCell: ({ row }: { row: Row }) => <CoverCell row={row} onOpen={() => openArticle(row)} />,
      },
      {
        field: 'wordCount',
        headerName: 'Words',
        type: 'string',
        width: 110,
        renderCell: ({ row }: { row: Row }) => row.wordCount.toLocaleString('en-US'),
      },
      {
        field: 'publishedAt',
        headerName: 'Published',
        type: 'string',
        width: 150,
        renderCell: ({ row }: { row: Row }) => formatIsoDay(row.publishedAt),
      },
      {
        field: 'updated',
        headerName: 'Last updated',
        type: 'string',
        width: 150,
        renderCell: ({ row }: { row: Row }) => formatIsoDay(row.updated),
      },
      {
        field: 'clicks',
        headerName: 'Clicks',
        type: 'string',
        width: 110,
        sortable: false,
        renderCell: ({ row }: { row: Row }) => (pagesLoading ? <PerfSkeleton /> : count(row.perf?.clicks)),
      },
      {
        field: 'impressions',
        headerName: 'Impressions',
        type: 'string',
        width: 140,
        sortable: false,
        renderCell: ({ row }: { row: Row }) => (pagesLoading ? <PerfSkeleton /> : count(row.perf?.impressions)),
      },
      {
        field: 'visitors',
        headerName: 'Visitors',
        type: 'string',
        width: 120,
        sortable: false,
        renderCell: ({ row }: { row: Row }) => (pagesLoading ? <PerfSkeleton /> : count(row.perf?.visitors)),
      },
      {
        field: 'link',
        headerName: 'Link',
        type: 'string',
        width: 120,
        sortable: false,
        renderCell: ({ row }: { row: Row }) => {
          const link = articleLink({ path: row.path, href: row.href, status: row.state === 'published' ? 'published' : 'staged' })
          return (
            <Stack
              component="a"
              href={link.href}
              target="_blank"
              rel="noreferrer"
              direction="row"
              alignItems="center"
              spacing={1}
              aria-label={`${link.label}: ${row.title} (opens in a new tab)`}
              sx={{ color: 'inherit', textDecoration: 'none', '&:hover .art-link': { textDecoration: 'underline' } }}
            >
              <Typography className="art-link" variant="body3">
                {link.label}
              </Typography>
              <ArrowUpRightIcon strokeWidth={1} size={14} style={{ opacity: 0.6 }} />
            </Stack>
          )
        },
      },
      {
        field: 'actions',
        headerName: false,
        disableReorder: true,
        sortable: false,
        width: 56,
        align: 'center',
        pinned: 'right',
        renderCell: ({ row }: { row: Row }) => (
          <ActionDropdown
            items={[
              {
                // VRG-733 — the article page (Overview, Cover); the title cell opens it too.
                label: 'Open',
                icon: <FileTextIcon strokeWidth={1} />,
                onClick: () => openArticle(row),
              },
              {
                // VRG-733 — generate (or review) the piece's cover: articles and explainers (2026-11-04).
                label: 'Cover',
                icon: <ImageIcon strokeWidth={1} />,
                visible: articleHasCoverTool(row.section),
                onClick: () => navigate(ROUTES.INTERNAL_GTM_ARTICLE_COVER(row.section, row.slug)),
              },
              {
                // VRG-685 — live on vera.inc within a minute. Hidden once live (§46: record state).
                label: 'Publish',
                icon: <GlobeIcon strokeWidth={1} />,
                visible: row.state !== 'published',
                disabled: !canPublish || publishBusy,
                tooltip: canPublish ? undefined : PUBLISH_PERMISSION_TOOLTIP,
                onClick: () => setPublishing(row),
              },
              {
                label: row.state === 'changed' ? 'Approve this version' : 'Approve',
                icon: <BadgeCheckIcon strokeWidth={1} />,
                // Record state hides (§46): nothing to approve once live, or when the current
                // version is already the approved one.
                visible: row.state === 'needs_review' || row.state === 'changed',
                disabled: !isPlatformAdmin,
                tooltip: isPlatformAdmin ? undefined : 'Only a Vera admin can approve an article.',
                onClick: () => setConfirming(row),
              },
              {
                label: 'Withdraw approval',
                icon: <Undo2Icon strokeWidth={1} />,
                visible: !!row.approval && row.state !== 'published',
                disabled: !isPlatformAdmin || revoke.isPending,
                tooltip: isPlatformAdmin ? undefined : 'Only a Vera admin can withdraw an approval.',
                onClick: () =>
                  revoke.mutate(
                    { section: row.section, slug: row.slug },
                    {
                      onSuccess: () => notify({ type: 'success', text: `Approval withdrawn: ${row.title}` }),
                      onError: (e) => notify({ type: 'error', text: (e as Error).message }),
                    },
                  ),
              },
              {
                // Re-sends the signed webhook for the CURRENT state; no version bump, no confirm
                // (it changes nothing on the website that the published list does not already say).
                label: 'Retry website refresh',
                icon: <RefreshCwIcon strokeWidth={1} />,
                visible: row.refreshPending,
                disabled: !canPublish || publishBusy,
                tooltip: canPublish ? undefined : PUBLISH_PERMISSION_TOOLTIP,
                onClick: () =>
                  refresh.mutate(
                    { section: row.section, slug: row.slug },
                    {
                      onSuccess: (p) =>
                        notify(
                          p.revalidate.status === 'sent'
                            ? { type: 'success', text: `Website refreshed: ${row.title}` }
                            : { type: 'warning', text: `Website still not confirmed: ${p.revalidate.error ?? 'no response'}` },
                        ),
                      onError: (e) => notify({ type: 'error', text: (e as Error).message }),
                    },
                  ),
              },
              {
                label: 'Unpublish',
                icon: <EyeOffIcon strokeWidth={1} />,
                // Not `destructive` (§32/§73): nothing ceases to exist and Publish undoes it. The
                // confirm carries the consequence (broken links, sitemap) instead of the colour.
                visible: !!row.publication?.isPublished,
                disabled: !canPublish || publishBusy,
                tooltip: canPublish ? undefined : PUBLISH_PERMISSION_TOOLTIP,
                onClick: () => setUnpublishing(row),
              },
            ]}
          />
        ),
      },
    ],
    [isPlatformAdmin, canPublish, publishBusy, pagesLoading, revoke, refresh, notify, navigate],
  )

  return (
    <>
      {/* The source line came off the page (Spencer, 2026-11-04). A failed fresh read still says so,
          because the Status column would otherwise be stale without a word. */}
      {website && !website.available ? (
        <Typography variant="body3" color="text.secondary" sx={{ display: 'block', mb: 4 }}>
          The website could not be read ({website.reason}), so this list is the last snapshot.
        </Typography>
      ) : null}
      <Table
        fillBottom
        loading={approvalsLoading || publicationsLoading || websiteLoading}
        rows={rows}
        columns={columns}
        getRowId={(row: Row) => row.id}
        defaultSortModel={[{ field: 'publishedAt', sort: 'desc' }]}
        exportFilename="articles"
        emptyState={
          <EmptyState
            title={
              approvalsError || publicationsError
                ? `Could not load ${approvalsError ? 'approvals' : 'publication state'}`
                : 'No articles match'
            }
            text={
              approvalsError || publicationsError
                ? ((approvalsError ?? publicationsError) as Error).message
                : 'Clear the search or a filter to see every article.'
            }
            icon={<FileTextIcon strokeWidth={1} size={32} />}
          />
        }
      />
      <ConfirmSettingModal
        open={!!confirming}
        onClose={() => setConfirming(null)}
        title="Approve this article?"
        consequence={
          confirming
            ? `This records that "${confirming.title}" is approved as it stands in the website snapshot. It does not publish it: the article stays staged until you publish it. If the words change before then, it will show as changed since approval.`
            : ''
        }
        confirmLabel="Approve article"
        loading={approve.isPending}
        onConfirm={() => {
          if (!confirming) return
          const row = confirming
          approve.mutate(
            { section: row.section, slug: row.slug, contentHash: row.contentHash, title: row.title },
            {
              onSuccess: () => {
                notify({ type: 'success', text: `Approved “${row.title}”. Awaiting publish.` })
                setConfirming(null)
              },
              onError: (e) => notify({ type: 'error', text: (e as Error).message }),
            },
          )
        }}
      />
      {/* VRG-685 — §43: both effects land on the public website, so each names its consequence. */}
      <ConfirmSettingModal
        open={!!publishing}
        onClose={() => setPublishing(null)}
        title={publishing ? publishTitle(publishing.title) : ''}
        preamble={
          publishing?.state === 'changed' && publishing.approval
            ? `This article changed since it was approved on ${day(publishing.approval.approvedAt)}. What goes live is not the version that was approved.`
            : undefined
        }
        consequence={publishing ? publishConsequence(publishing.section) : ''}
        confirmLabel="Publish article"
        loading={publish.isPending}
        onConfirm={() => {
          if (!publishing) return
          const row = publishing
          publish.mutate(
            { section: row.section, slug: row.slug, title: row.title, contentHash: row.contentHash },
            {
              onSuccess: (p) => {
                notify(
                  p.revalidate.status === 'sent'
                    ? { type: 'success', text: `Published “${row.title}”. Live on vera.inc within a minute.` }
                    : {
                        type: 'warning',
                        text: `Published “${row.title}”. Website refresh pending: vera.inc picks it up within a minute.`,
                      },
                )
                setPublishing(null)
              },
              onError: (e) => notify({ type: 'error', text: (e as Error).message }),
            },
          )
        }}
      />
      <ConfirmSettingModal
        open={!!unpublishing}
        onClose={() => setUnpublishing(null)}
        title={unpublishing ? unpublishTitle(unpublishing.title) : ''}
        consequence={UNPUBLISH_CONSEQUENCE}
        confirmLabel="Unpublish article"
        loading={unpublish.isPending}
        onConfirm={() => {
          if (!unpublishing) return
          const row = unpublishing
          unpublish.mutate(
            { section: row.section, slug: row.slug },
            {
              onSuccess: (p) => {
                notify(
                  p.revalidate.status === 'sent'
                    ? { type: 'success', text: `Unpublished “${row.title}”.` }
                    : {
                        type: 'warning',
                        text: `Unpublished “${row.title}”. Website refresh pending: vera.inc drops it within a minute.`,
                      },
                )
                setUnpublishing(null)
              },
              onError: (e) => notify({ type: 'error', text: (e as Error).message }),
            },
          )
        }}
      />
    </>
  )
}

/** VRG-733 — the cover as the website has it: a thumbnail (through the authenticated API) and its kind. */
const CoverCell = ({ row, onOpen }: { row: Row; onOpen: () => void }) => {
  const { src } = useCoverImageUrl(row.cover.type === 'image' ? articleContentApi.coverUrl(row.section, row.slug, 160) : null)
  return (
    <Stack direction="row" alignItems="center" spacing={2} height="100%" sx={{ minWidth: 0, cursor: 'pointer' }} onClick={onOpen}>
      {row.cover.type === 'image' ? (
        <Box sx={{ width: 64, height: 36, flexShrink: 0, borderRadius: '4px', overflow: 'hidden', backgroundColor: '#0e0d12' }}>
          {src ? <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} /> : null}
        </Box>
      ) : null}
      <Typography variant="body3" noWrap color={row.cover.type === 'image' ? 'text.primary' : 'text.secondary'}>
        {coverLabel(row.cover, row.section)}
      </Typography>
    </Stack>
  )
}

/*
  VRG-751 — the two Insights tabs that live in this directory. Each renders the page Header
  itself, with the parent's title and the parent's single tab row, so each keeps its own header
  action (one primary, rightmost, §26).

  Until VRG-751 one component owned both, plus a THIRD tab (VERAfied guests, VRG-740) and a tab
  row of its own nested under the parent's. The nested row is gone, Ideas is a top-level tab
  because ideas feed articles AND interviews, and the second guest list is retired by VRG-752.
*/

const ARTICLES_DESCRIPTION =
  'Every /insights and /learn piece. Everything stays staged until you publish it here; publishing goes live on vera.inc within a minute. Approving records your decision without publishing.'

const IDEAS_DESCRIPTION =
  'Pieces worth writing, highest priority first, for articles and for VERAfied interviews alike. New ones arrive after each SEO recompute from competitor gaps, rising terms with no page and AI answers that skip Vera, or pull them now. Approve or reject each one.'

type TabProps = { title: string; tabs: ReactNode }

/** Insights → Articles. No header action: publishing and approving are per-row (§26). */
export const ArticlesTab = ({ title, tabs }: TabProps) => (
  <>
    <Header title={title} description={ARTICLES_DESCRIPTION} />
    {tabs}
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <ArticlesTable />
    </Box>
  </>
)

/**
 * The outcome of a feed run, said in one line. `created: 0` is the COMMON and correct answer once
 * every open SEO recommendation already has an idea, so it must not read as a failure — the
 * skipped tally is what distinguishes "nothing to find" from "nothing ran".
 */
const feedMessage = (run: ContentFeedRun): string => {
  // POST .../feed runs BOTH halves and returns the whole run; this button is "Pull from SEO", so
  // it reports the SEO half — `run.seo` — not the corpus totals beside it. The half is nullable
  // because a run can be corpus-only, and saying "no new ideas" when the SEO feed never ran would
  // be the exact confusion the docblock above guards against.
  const r = run.seo
  if (!r) return 'The SEO feed did not run.'
  const skipped = Object.values(r.skipped).reduce((t, n) => t + n, 0)
  if (r.created === 0)
    return skipped > 0
      ? `No new ideas. ${skipped} SEO recommendation${skipped === 1 ? '' : 's'} already had one, or were dismissed.`
      : 'No new ideas: nothing in SEO is unaccounted for.'
  const terms = r.terms_added > 0 ? `, ${r.terms_added} target term${r.terms_added === 1 ? '' : 's'} added` : ''
  return `${r.created} new idea${r.created === 1 ? '' : 's'} from SEO${terms}.`
}

/**
 * Insights → Ideas (VRG-740). "New idea" is the primary, rightmost; gated on managing ideas.
 *
 * VRG-765 (Spencer, 2026-10-29) adds "Pull from SEO" beside it, as a SECONDARY — the whole chain
 * already existed and ended one link short: the feed (`content-ideas.feed.ts`), the service, the
 * `POST content-ideas/feed` route, the api client call and `useFeedContentIdeas` were all built
 * and the hook had NO call site, so the only way ideas arrived was as a side effect of an SEO
 * Recommendations recompute. Nothing on the page said that, and nothing let you ask for it.
 *
 * It is database-only — it reads `seo_recommendations` and the keyword master and writes ideas. No
 * credits, no external call, so it needs no cost warning and no confirm.
 */
export const ContentIdeasTab = ({ title, tabs }: TabProps) => {
  const { isHasAccess: canManage } = useCanAccess({ action: MANAGE_IDEAS_PERMISSION })
  const [creating, setCreating] = useState(false)
  const { open: notify } = useSnackbar()
  const feed = useFeedContentIdeas()

  const pull = async () => {
    try {
      notify({ type: 'success', text: feedMessage(await feed.mutateAsync()) })
    } catch (e) {
      notify({ type: 'error', text: (e as Error).message })
    }
  }

  return (
    <>
      <Header
        title={title}
        description={IDEAS_DESCRIPTION}
        rightComponents={
          <Stack direction="row">
            <Tooltip title={canManage ? 'Read the open SEO recommendations and add an idea for any that has none. No credits.' : MANAGE_IDEAS_TIP}>
              <span style={{ display: 'inline-flex' }}>
                <Button
                  variant="secondary"
                  disabled={!canManage || feed.isPending}
                  onClick={pull}
                  startIcon={<RefreshCwIcon strokeWidth={1} />}
                >
                  {feed.isPending ? 'Pulling…' : 'Pull from SEO'}
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={canManage ? '' : MANAGE_IDEAS_TIP}>
              <span style={{ display: 'inline-flex' }}>
                <Button
                  variant="primary"
                  disableElevation
                  disabled={!canManage}
                  onClick={() => setCreating(true)}
                  startIcon={<PlusIcon strokeWidth={1} />}
                >
                  New idea
                </Button>
              </span>
            </Tooltip>
          </Stack>
        }
      />
      {tabs}
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <ContentIdeasPanel creating={creating} onCreatingChange={setCreating} />
      </Box>
    </>
  )
}

/**
 * Insights → Rules. VRG-741's editorial-rules snapshot, read-only: the canon is the brand repo
 * and this is its synced copy, the same text the future writer reads. It was ARTICLES_TABS'
 * fourth tab before VRG-751 replaced that tab model; kept here so the feature did not vanish
 * with the model that hosted it. No header action — there is nothing to do on this tab.
 */
export const RulesTab = ({ title, tabs }: TabProps) => (
  <>
    <Header
      title={title}
      description="The editorial rules every piece follows, read-only. The canon is the brand repo. This is its synced snapshot."
    />
    {tabs}
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <EditorialRulesPanel />
    </Box>
  </>
)
